import { useRef, useState } from "react";
import { useParams } from "react-router-dom";
import * as Yup from "yup";
import {
  empty,
  prepareResponseData,
  isObject,
  isString,
  isArray,
  errorResponse,
} from "../../Utilities/utils";
import { Toast } from "primereact/toast";
import { Form, Formik } from "formik";

// css
import "../students/Students.css";
import "../dashboard/Dashboard.css";
import "../staff/Staff.css";
import "./Parents.css";

// api
import parentApi from "../../api/Parents";
import studentApi from "../../api/Student";

// components
import MainHeader from "../../components/headers/mainHeader/MainHeader";
import AppWrapper from "../../components/appWrapper/AppWrapper";
import Card from "../../components/card/Card";
import { useContext } from "react";
import { AuthContext } from "../Root/ProtectedRoute";
import ButtonIcon from "../../components/buttons/buttonIcon/ButtonIcon";
import FullPageLoader from "../../components/loader/FullPageLoader";
import InputField from "../../components/form/InputField";
import ItemCard from "../../components/card/itemCard/ItemCard";

const validationSchema = Yup.object().shape({
  search_value: Yup.string().optional(),
});

const initialValues = {
  search_value: "",
};

const AssignChildrenToParent = ({ ...props }) => {
  const { user } = useContext(AuthContext);
  const schoolId = !empty(user) && !empty(user._id) ? user._id : "";
  const [students, setStudents] = useState([]);
  const [searchValue, setSearchValue] = useState("");

  const [isLoading, setIsLoading] = useState(false); // (WIP)
  const { parentId } = useParams();
  const toastTR = useRef(null);

  // alert functions
  const responseDialog = (severity = null, summary = null, detail = null) => {
    toastTR?.current?.show({
      severity,
      summary,
      detail,
      life: 8000,
    });
  };

  /**
   * get students
   * @param {*} values
   * @returns
   */
  const getStudents = async (values) => {
    // get the searched student details
    try {
      if (!isLoading) setIsLoading(true);
      if (empty(values)) {
        return responseDialog(
          "error",
          "Internal Error",
          "Something went wrong. Please refresh the page or try again."
        );
      }
      const searchValue =
        !empty(values.search_value) && isString(values.search_value)
          ? values.search_value
          : "";
      const response = await studentApi.getStudents(
        schoolId,
        null,
        null,
        searchValue
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || empty(response_data.success)) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to update qr-code!"
        );
      }

      let students = [];
      if (
        !empty(response_data) &&
        !empty(response_data.response) &&
        isArray(response_data.response)
      ) {
        students = response_data.response;
      }

      setSearchValue(searchValue);
      // get students
      setStudents(students);
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * assign student to parent
   * @returns
   */
  const assignStudentToParent = async (studentId, currentStatus) => {
    // assign student to parent
    try {
      if (!isLoading) setIsLoading(true);

      const response = await parentApi.assignStudentToParent(
        schoolId,
        parentId,
        studentId,
        currentStatus
      );
      const response_data = prepareResponseData(response);
      if (empty(response_data) || empty(response_data.success)) {
        return responseDialog(
          "error",
          "Error Alert",
          !empty(response_data) && !empty(response_data.response)
            ? response_data.response
            : "Failed to assign student to parent!"
        );
      }

      responseDialog(
        "success",
        "Success",
        "Assigned student to parent successfully."
      );

      return getStudents({ search_value: searchValue });
    } catch (error) {
      responseDialog("error", "Error Alert!", errorResponse(error));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <AppWrapper {...props}>
        <main>
          {/* menu header */}
          <MainHeader title="Assign Children/Ward(s) to Parents" />
          {/* end menu header */}
          <div
            className="mt-10"
            style={{ display: "flex", flexDirection: "row" }}
          >
            <div className="chart_box">
              <Card
                children={
                  <>
                    <MainHeader title="Search for Student(s)" />
                    <div className="dashboard_school_list">
                      <div className="avatar-container flex">
                        <div className="mb-20" style={{ width: "100%" }}>
                          <Formik
                            enableReinitialize
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            onSubmit={getStudents}
                          >
                            <Form>
                              <div className="flex justify-center">
                                <InputField
                                  placeholder="Enter Student First, Last, Middle name or Reg. No."
                                  name="search_value"
                                  height={50}
                                  containerWidth="100%"
                                />
                                <ButtonIcon
                                  height={45}
                                  color="#ffffff"
                                  marginLeft={2}
                                  marginTop={2}
                                  backgroundColor="#633ccd"
                                  width={280}
                                  borderColor="#633ccd"
                                  buttonText="Search"
                                  type="submit"
                                />
                              </div>
                            </Form>
                          </Formik>
                        </div>
                      </div>
                      <div className="mt-40 mb-20">
                        <MainHeader title="Search Results" />
                      </div>
                      <div
                        className="flex row-g-20 col-g-18"
                        style={{
                          maxWidth: "100%",
                          flexWrap: "wrap",
                        }}
                      >
                        {!empty(students) && isArray(students)
                          ? students.map((item, index) => {
                              if (!empty(item) && isObject(item)) {
                                const avatar =
                                  !empty(item.avatar) && isString(item.avatar)
                                    ? item.avatar
                                    : "";
                                const classTitle =
                                  !empty(item.class) && isString(item.class)
                                    ? item.class
                                    : "";
                                const fullName =
                                  !empty(item.fullName) &&
                                  isString(item.fullName)
                                    ? item.fullName
                                    : "";
                                const regNo =
                                  !empty(item.regNo) && isString(item.regNo)
                                    ? item.regNo
                                    : "";
                                const parents =
                                  !empty(item.parents) && isArray(item.parents)
                                    ? item.parents
                                    : [];
                                let childOf =
                                  !empty(parents) && parents.includes(parentId);
                                let studentId = !empty(item._id)
                                  ? item._id
                                  : "";
                                return (
                                  <ItemCard
                                    img={avatar}
                                    width="24%"
                                    key={index}
                                    classTitle={classTitle}
                                    fullName={fullName}
                                    regNo={regNo}
                                    floatingButtonActive={childOf}
                                    floatingButtonAction={() =>
                                      assignStudentToParent(studentId, childOf)
                                    }
                                  />
                                );
                              }
                            })
                          : ""}
                      </div>
                    </div>
                  </>
                }
                addStyle="student_list_box"
              />
            </div>
            {/* end chart */}
          </div>
        </main>
        <Toast ref={toastTR} position="bottom-left" />
        {isLoading && <FullPageLoader visible={isLoading} />}
      </AppWrapper>
      <Toast ref={toastTR} style={{ zIndex: 99999 }} position="bottom-left" />
    </>
  );
};

export default AssignChildrenToParent;
